import React from "react";
import { MdEventSeat } from "react-icons/md";
import { FaBusSimple, FaTrain, FaArrowRightLong } from "react-icons/fa6";

/**
 * The journey as the passenger will travel it.
 *
 * The order page showed the whole booking as two stations and a list of seat
 * numbers. On ORDE-364 -- a round trip with two changes each way -- that was
 * six seats in a row and no indication of which coach, which bus, where to
 * change or how long they had to do it.
 *
 * All of it is already in the confirmed order. `transfers[]` carries each
 * change with its arrival, its departure, the wait between them and FlixBus's
 * own note about getting from one to the other; `seats_per_relation` carries
 * the seat for each ride. Nothing had read them.
 *
 * Worth knowing: these are the per-leg times the SEARCH cannot give us -- that
 * is the open question with FlixBus (PENDING P93). They exist once an order is
 * confirmed, so the order page can be complete even while the results list
 * cannot.
 */

type Stamp = { timestamp?: number; tz?: string } | undefined;

/**
 * A time as it will read at that station.
 *
 * Every stamp comes with the station's own offset. Formatting in the browser's
 * timezone would tell somebody in India that their Berlin coach leaves at
 * 20:10 -- correct, and useless.
 */
const atStation = (s: Stamp): string => {
  if (!s?.timestamp) return "";
  const offsetMinutes = (() => {
    const m = String(s.tz || "").match(/([+-])(\d{2}):?(\d{2})/);
    if (!m) return 0;
    return (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3]));
  })();
  const shifted = new Date((s.timestamp + offsetMinutes * 60) * 1000);
  return shifted.toISOString().slice(11, 16);
};

const dayAt = (s: Stamp): string => {
  if (!s?.timestamp) return "";
  const m = String(s.tz || "").match(/([+-])(\d{2}):?(\d{2})/);
  const off = m ? (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) : 0;
  const d = new Date((s.timestamp + off * 60) * 1000);
  return d.toUTCString().slice(0, 11).trim();
};

const waitOf = (d: any): string => {
  const h = Number(d?.hour || 0);
  const m = Number(d?.minutes || 0);
  if (!h && !m) return "";
  return [h ? `${h}h` : "", m ? `${m}m` : ""].filter(Boolean).join(" ");
};

const vehicleIcon = (v: string) =>
  /train/i.test(v) ? (
    <FaTrain className="shrink-0 text-gray-600 dark:text-gray-300" />
  ) : (
    <FaBusSimple className="shrink-0 text-gray-600 dark:text-gray-300" />
  );

type Leg = {
  from: string;
  to: string;
  departure: Stamp;
  arrival: Stamp;
  vehicle: string;
  seats: string[];
  line: string;
};

type Change = { station: string; arrival: Stamp; departure: Stamp; wait: string; note: string };

type Trip = { title: string; legs: Leg[]; changes: Change[] };

/**
 * Build the legs from the stations, not from the seats.
 *
 * A ride with no seat map still has to appear -- a FlixTrain leg often has no
 * seat at all -- so the sequence comes from the endpoints plus each transfer,
 * and the seat is attached to whichever leg matches.
 */
export const buildTrips = (orderItem: any): Trip[] => {
  const trips = orderItem?.flixbusData?.orderInfo?.order?.trips;
  if (!Array.isArray(trips) || !trips.length) return [];

  return trips
    .map((t: any, index: number) => {
      const stations = [
        t?.departure_station,
        ...(t?.transfers || []).map((tr: any) => tr?.station),
        t?.arrival_station,
      ].filter(Boolean);
      if (stations.length < 2) return null;

      const seatFor = (fromId: any, toId: any) => {
        const rel = (t?.seats_per_relation || []).find(
          (r: any) => String(r?.from) === String(fromId) && String(r?.to) === String(toId),
        );
        const seats = (rel?.seats || []).filter((s: any) => s?.label);
        return {
          labels: seats.map((s: any) => String(s.label)),
          vehicle: String(seats[0]?.vehicle || ""),
        };
      };

      const transfers = t?.transfers || [];
      const legs: Leg[] = [];
      for (let i = 0; i < stations.length - 1; i++) {
        const a = stations[i];
        const b = stations[i + 1];
        const { labels, vehicle } = seatFor(a?.id, b?.id);
        legs.push({
          from: String(a?.name || ""),
          to: String(b?.name || ""),
          // The first leg leaves when the trip does; each later one leaves
          // when its transfer says, which is also the only place per-leg
          // times exist at all.
          departure: i === 0 ? t?.departure : transfers[i - 1]?.departure,
          arrival: i === stations.length - 2 ? t?.arrival : transfers[i]?.arrival,
          vehicle,
          seats: labels,
          line: String(
            (i === 0 ? t?.line_direction : transfers[i - 1]?.line_direction) || "",
          ),
        });
      }

      const changes: Change[] = transfers.map((tr: any) => ({
        station: String(tr?.station?.name || ""),
        arrival: tr?.arrival,
        departure: tr?.departure,
        wait: waitOf(tr?.duration),
        note: String(tr?.message || ""),
      }));

      return {
        title: trips.length > 1 ? (index === 0 ? "Outbound" : "Return") : "Journey",
        legs,
        changes,
      };
    })
    .filter(Boolean) as Trip[];
};

const JourneyBreakdown: React.FC<{ orderItem: any }> = ({ orderItem }) => {
  const trips = buildTrips(orderItem);
  if (!trips.length) return null;

  return (
    <div className="p-3 border dark:border-dark-foreground rounded-lg bg-gray-50 dark:bg-dark-background">
      <h5 className="flex items-center gap-2 text-md font-bold text-gray-800 dark:text-white underline">
        <MdEventSeat size="25" /> Journey &amp; seats
      </h5>

      <div className="mt-3 space-y-5">
        {trips.map((trip, ti) => (
          <div key={ti}>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
              {trip.title}
            </p>

            <ol className="border-l-2 border-gray-200 dark:border-gray-700 ml-2 space-y-3">
              {trip.legs.map((leg, i) => (
                <li key={i} className="relative pl-4">
                  <span className="absolute -left-[5px] top-2 w-2 h-2 rounded-full bg-gray-400 dark:bg-gray-500" />

                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                      {vehicleIcon(leg.vehicle)}
                      <span>{leg.from}</span>
                      <FaArrowRightLong className="w-3 h-3 text-gray-400 shrink-0" />
                      <span>{leg.to}</span>
                    </span>

                    {leg.seats.length > 0 && (
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap">
                        Seat {leg.seats.join(", ")}
                      </span>
                    )}
                  </div>

                  {(atStation(leg.departure) || atStation(leg.arrival)) && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 tabular-nums">
                      {atStation(leg.departure)} &rarr; {atStation(leg.arrival)}
                      {/* Say so when the leg lands on another day. */}
                      {dayAt(leg.departure) &&
                        dayAt(leg.arrival) &&
                        dayAt(leg.departure) !== dayAt(leg.arrival) && (
                          <span className="ml-1">({dayAt(leg.arrival)})</span>
                        )}
                    </div>
                  )}

                  {leg.line && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {leg.line}
                    </div>
                  )}

                  {/* The change that follows this leg. */}
                  {trip.changes[i] && (
                    <div className="mt-2 mb-1 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 px-2 py-1.5">
                      <p className="text-xs font-semibold text-amber-800 dark:text-amber-500">
                        Change at {trip.changes[i].station}
                        {trip.changes[i].wait && (
                          <span className="font-normal">
                            {" "}
                            &middot; {trip.changes[i].wait} to connect
                          </span>
                        )}
                      </p>
                      {(atStation(trip.changes[i].arrival) ||
                        atStation(trip.changes[i].departure)) && (
                        <p className="text-xs text-amber-800/80 dark:text-amber-500/80 tabular-nums">
                          Arrive {atStation(trip.changes[i].arrival)} &middot;
                          leave {atStation(trip.changes[i].departure)}
                        </p>
                      )}
                      {trip.changes[i].note && (
                        <p className="text-xs text-amber-800/80 dark:text-amber-500/80 mt-0.5">
                          {trip.changes[i].note}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>

      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
        Times are shown at each station&apos;s own local time.
      </p>
    </div>
  );
};

export default JourneyBreakdown;
